// src/context/LibraryContext.js
//
// This is the "plug in your own music and categorize it in one place" piece
// from the product notes — the feature meant to differentiate the app from
// generic rain-sound apps.
//
// Users import audio files (from their device, or later a streaming SDK),
// tag each one with one or more categories (Sleep / Study / Focus / custom),
// and the rest of the app reads from here instead of making users hunt
// through their music app every night.
//
// Persisted locally with AsyncStorage for the prototype. Swap for a backend
// sync layer later if you want cross-device libraries.

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import * as ImagePicker from 'expo-image-picker';
import { Alert, AppState } from 'react-native';
import { DEFAULT_CATEGORIES } from '../data/sounds';
import { AudioEngine } from '../audio/AudioEngine';

const STORAGE_KEY = 'sleepmix.library.v1';
export const ORPHAN_CLEANUP_KEY = 'sleepmix.orphan-cleanup.v1';
export const TRACKS_DIR = `${FileSystem.documentDirectory}sleepmix-tracks/`;
const ARTWORK_DIR = `${FileSystem.documentDirectory}sleepmix-artwork/`;
const LibraryContext = createContext(null);

/**
 * One-time orphan-cleanup pass: deletes files inside `sleepmix-tracks/` that
 * are no longer referenced by any library track. Runs only when the cleanup
 * flag is absent from AsyncStorage. Exported so it can be exercised directly
 * in unit tests without mounting the full provider.
 *
 * Reinstall safety: on iOS, `documentDirectory` (and its files) survives an
 * app reinstall even though AsyncStorage is wiped. This means the cleanup flag
 * is absent after reinstall, and the library track list will be empty. Passing
 * an empty array here is intentionally correct — every file in the tracks
 * directory is a true orphan because there is no library metadata pointing to
 * any of them. Deleting them is safe; any tracks the user re-imports after the
 * reinstall create brand-new files and are never touched by this cleanup.
 *
 * @param {Array<{uri: string}>} currentTracks  Current library track list.
 *   Pass an empty array when the library has just been wiped (e.g. reinstall).
 */
export async function runOrphanCleanupPass(currentTracks) {
  const cleanupDone = await AsyncStorage.getItem(ORPHAN_CLEANUP_KEY);
  if (!cleanupDone) {
    try {
      const dirInfo = await FileSystem.getInfoAsync(TRACKS_DIR);
      if (dirInfo.exists) {
        const files = await FileSystem.readDirectoryAsync(TRACKS_DIR);
        const referencedUris = new Set(currentTracks.map((t) => t.uri));
        await Promise.all(
          files.map(async (filename) => {
            const filePath = `${TRACKS_DIR}${filename}`;
            if (!referencedUris.has(filePath)) {
              await FileSystem.deleteAsync(filePath, { idempotent: true });
            }
          })
        );
      }
    } catch (err) {
      console.warn('[LibraryContext] Orphan cleanup failed:', err);
    }
    await AsyncStorage.setItem(ORPHAN_CLEANUP_KEY, 'done');
  }
}

/** Ensure the permanent tracks directory exists. */
async function ensureTracksDir() {
  const info = await FileSystem.getInfoAsync(TRACKS_DIR);
  if (!info.exists) {
    await FileSystem.makeDirectoryAsync(TRACKS_DIR, { intermediates: true });
  }
}

/** Ensure the permanent artwork directory exists. */
async function ensureArtworkDir() {
  const info = await FileSystem.getInfoAsync(ARTWORK_DIR);
  if (!info.exists) {
    await FileSystem.makeDirectoryAsync(ARTWORK_DIR, { intermediates: true });
  }
}

/**
 * Copy a file to the permanent tracks directory.
 * Returns the new permanent URI.
 */
async function copyToDocuments(sourceUri, filename) {
  await ensureTracksDir();
  const dest = `${TRACKS_DIR}${filename}`;
  await FileSystem.copyAsync({ from: sourceUri, to: dest });
  return dest;
}

/**
 * Copy an image file to the permanent artwork directory.
 * Returns the new permanent URI.
 */
async function copyToArtworkDir(sourceUri, filename) {
  await ensureArtworkDir();
  const dest = `${ARTWORK_DIR}${filename}`;
  await FileSystem.copyAsync({ from: sourceUri, to: dest });
  return dest;
}

/**
 * Ask the user if they want to pick a cover image for a newly imported track.
 * Returns a permanent file URI string, or null if skipped/cancelled.
 */
async function pickCoverArt(trackId) {
  return new Promise((resolve) => {
    Alert.alert(
      'Add cover art?',
      'Pick a photo from your library to show on the lock screen when this track plays.',
      [
        { text: 'Skip', style: 'cancel', onPress: () => resolve(null) },
        {
          text: 'Choose Photo',
          onPress: async () => {
            try {
              const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
              if (status !== 'granted') {
                Alert.alert('Permission needed', 'Allow photo access in Settings to pick cover art.');
                resolve(null);
                return;
              }
              const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsEditing: true,
                aspect: [1, 1],
                quality: 0.8,
              });
              if (result.canceled || !result.assets?.[0]?.uri) {
                resolve(null);
                return;
              }
              const sourceUri = result.assets[0].uri;
              const ext = sourceUri.split('.').pop()?.split('?')[0] || 'jpg';
              const filename = `artwork-${trackId}.${ext}`;
              try {
                const permanentUri = await copyToArtworkDir(sourceUri, filename);
                resolve(permanentUri);
              } catch (err) {
                console.warn('[LibraryContext] Could not copy cover art:', err);
                resolve(null);
              }
            } catch (err) {
              console.warn('[LibraryContext] Cover art picker error:', err);
              resolve(null);
            }
          },
        },
      ]
    );
  });
}

export function LibraryProvider({ children }) {
  const [tracks, setTracks] = useState([]); // { id, uri, name, categories: [] }
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loaded, setLoaded] = useState(false);

  // Always-current mirror of tracks so callbacks that don't list tracks in
  // their dependency array (to avoid excess re-creation) can still read the
  // latest value without relying on state updater side-effects.
  const tracksRef = useRef(tracks);
  useEffect(() => { tracksRef.current = tracks; }, [tracks]);

  useEffect(() => {
    (async () => {
      const raw = await AsyncStorage.getItem(STORAGE_KEY);
      let finalTracks = [];

      if (raw) {
        const parsed = JSON.parse(raw);
        let loadedTracks = parsed.tracks || [];

        // Migrate any tracks still pointing at the cache directory to the
        // permanent documents directory. If the cached file is already gone
        // the track is flagged so the UI can prompt re-import.
        const cacheDir = FileSystem.cacheDirectory;
        let migrationOccurred = false;
        const migratedTracks = await Promise.all(
          loadedTracks.map(async (track) => {
            if (cacheDir && track.uri && track.uri.startsWith(cacheDir)) {
              try {
                const filename = track.uri.split('/').pop();
                const permanentUri = await copyToDocuments(track.uri, filename);
                migrationOccurred = true;
                return { ...track, uri: permanentUri };
              } catch {
                // Cached file is gone — mark so the UI can surface a warning.
                migrationOccurred = true;
                return { ...track, needsReimport: true };
              }
            }
            return track;
          })
        );

        setTracks(migratedTracks);
        setCategories(parsed.categories || DEFAULT_CATEGORIES);

        if (migrationOccurred) {
          await AsyncStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ tracks: migratedTracks, categories: parsed.categories || DEFAULT_CATEGORIES })
          );
        }

        finalTracks = migratedTracks;
      }

      // One-time cleanup: delete any files in the tracks directory that are no
      // longer referenced by a library track. Runs unconditionally (outside the
      // `if (raw)` guard) so it also fires after a reinstall, when AsyncStorage
      // is wiped but documentDirectory files may still be present. In that case
      // finalTracks is [], every file is treated as an orphan, and the directory
      // is fully cleared — which is correct because the user has no library
      // metadata pointing to those files and would need to re-import anyway.
      await runOrphanCleanupPass(finalTracks);

      setLoaded(true);
    })();
  }, []);

  const persist = useCallback(async (nextTracks, nextCategories) => {
    await AsyncStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ tracks: nextTracks, categories: nextCategories })
    );
  }, []);

  const importTrack = useCallback(async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: 'audio/*',
      copyToCacheDirectory: true,
    });
    if (result.canceled) return null;

    const file = result.assets[0];

    // Derive a unique filename so concurrent imports of same-named files don't
    // collide, then copy to permanent documents storage right away.
    const id = `${Date.now()}-${file.name}`;
    const safeFilename = id.replace(/[^a-zA-Z0-9._-]/g, '_');
    let permanentUri = file.uri;
    try {
      permanentUri = await copyToDocuments(file.uri, safeFilename);
    } catch (err) {
      // If the copy fails, fall back to the cache URI so the track is still
      // usable in the current session; it will be migrated on next load if the
      // file is still present.
      console.warn('[LibraryContext] Could not copy to documents dir:', err);
    }

    // Optionally let the user pick a cover image for the lock screen.
    const artworkUri = await pickCoverArt(id);

    const newTrack = {
      id,
      uri: permanentUri,
      name: file.name.replace(/\.[^/.]+$/, ''),
      categories: [],
      addedAt: Date.now(),
      artworkUri: artworkUri ?? undefined,
    };

    setTracks((prev) => {
      const next = [...prev, newTrack];
      persist(next, categories);
      return next;
    });

    return newTrack;
  }, [categories, persist]);

  /**
   * Re-import a track whose file can no longer be found.
   * Opens the document picker, copies the selected file to permanent storage,
   * updates the track's URI, and clears the needsReimport flag.
   *
   * If the track is currently active in AudioEngine (e.g. playing as part of a
   * mix on another screen), each matching layer is seamlessly crossfaded to the
   * new file so the mix continues without an audible gap or error.
   *
   * Returns the updated track on success, or null if the user cancelled.
   */
  const reimportTrack = useCallback(
    async (id) => {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'audio/*',
        copyToCacheDirectory: true,
      });
      if (result.canceled) return null;

      const file = result.assets[0];
      const safeFilename = `${Date.now()}-${file.name}`.replace(/[^a-zA-Z0-9._-]/g, '_');
      let permanentUri = file.uri;
      try {
        permanentUri = await copyToDocuments(file.uri, safeFilename);
      } catch (err) {
        console.warn('[LibraryContext] reimportTrack: could not copy to documents dir:', err);
      }

      // Read the track's current URI from the stable ref BEFORE mutating state.
      // Using tracksRef.current here is deterministic — it's never stale relative
      // to the call site, and it avoids the anti-pattern of mutating outer
      // variables from inside a setTracks updater (which React may call more than
      // once in StrictMode).
      const existingTrack = tracksRef.current.find((t) => t.id === id);
      const oldUri = existingTrack?.uri ?? null;
      const updated = existingTrack
        ? { ...existingTrack, uri: permanentUri, needsReimport: false }
        : null;

      if (!existingTrack) return null; // track was removed while picker was open

      setTracks((prev) => {
        const next = prev.map((t) =>
          t.id === id ? { ...t, uri: permanentUri, needsReimport: false } : t
        );
        persist(next, categories);
        return next;
      });

      // Seamlessly swap the file on any active layer so the mix keeps playing.
      if (oldUri && permanentUri !== oldUri) {
        AudioEngine.updateActiveLayersByUri(oldUri, permanentUri).catch((err) => {
          console.warn('[LibraryContext] reimportTrack: live layer update failed:', err);
        });
      }

      return updated;
    },
    [categories, persist]
  );

  const removeTrack = useCallback(
    (id) => {
      setTracks((prev) => {
        const track = prev.find((t) => t.id === id);
        // Delete the audio file from permanent storage if it lives in our tracks dir.
        if (track && track.uri && track.uri.startsWith(TRACKS_DIR)) {
          FileSystem.deleteAsync(track.uri, { idempotent: true }).catch((err) => {
            console.warn('[LibraryContext] removeTrack: could not delete file:', err);
          });
        }
        // Delete the artwork file from permanent storage if one was chosen.
        if (track && track.artworkUri && track.artworkUri.startsWith(ARTWORK_DIR)) {
          FileSystem.deleteAsync(track.artworkUri, { idempotent: true }).catch((err) => {
            console.warn('[LibraryContext] removeTrack: could not delete artwork:', err);
          });
        }
        const next = prev.filter((t) => t.id !== id);
        persist(next, categories);
        return next;
      });
    },
    [categories, persist]
  );

  /**
   * Replace or clear the cover art for an existing track.
   * If the track previously had artwork stored in our managed directory,
   * the old file is deleted before the new URI is saved.
   *
   * @param {string}      id           Track id.
   * @param {string|null} newArtworkUri Permanent URI of the new image, or null
   *                                    to remove artwork entirely.
   */
  const updateTrackArtwork = useCallback(
    async (id, newArtworkUri) => {
      const existing = tracksRef.current.find((t) => t.id === id);
      if (!existing) return;

      // Delete the old artwork file so it doesn't accumulate on disk.
      if (existing.artworkUri && existing.artworkUri.startsWith(ARTWORK_DIR)) {
        FileSystem.deleteAsync(existing.artworkUri, { idempotent: true }).catch((err) => {
          console.warn('[LibraryContext] updateTrackArtwork: could not delete old artwork:', err);
        });
      }

      setTracks((prev) => {
        const next = prev.map((t) => {
          if (t.id !== id) return t;
          const updated = { ...t };
          if (newArtworkUri) {
            updated.artworkUri = newArtworkUri;
          } else {
            delete updated.artworkUri;
          }
          return updated;
        });
        persist(next, categories);
        return next;
      });
    },
    [categories, persist]
  );

  /**
   * Show an action sheet that lets the user choose a new cover photo or
   * remove the existing one. Handles permissions, image-picker launch,
   * file copy, and calls updateTrackArtwork when done.
   *
   * @param {string} id Track id.
   */
  const editTrackArtwork = useCallback(
    (id) => {
      const track = tracksRef.current.find((t) => t.id === id);
      if (!track) return;

      const hasArtwork = !!track.artworkUri;

      const doPickPhoto = async () => {
        try {
          const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
          if (status !== 'granted') {
            Alert.alert('Permission needed', 'Allow photo access in Settings to pick cover art.');
            return;
          }
          const result = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ImagePicker.MediaTypeOptions.Images,
            allowsEditing: true,
            aspect: [1, 1],
            quality: 0.8,
          });
          if (result.canceled || !result.assets?.[0]?.uri) return;
          const sourceUri = result.assets[0].uri;
          const ext = sourceUri.split('.').pop()?.split('?')[0] || 'jpg';
          // Include a timestamp so re-picks with the same extension get a fresh file.
          const filename = `artwork-${id}-${Date.now()}.${ext}`;
          try {
            const permanentUri = await copyToArtworkDir(sourceUri, filename);
            await updateTrackArtwork(id, permanentUri);
          } catch (err) {
            console.warn('[LibraryContext] editTrackArtwork: could not copy cover art:', err);
          }
        } catch (err) {
          console.warn('[LibraryContext] editTrackArtwork: picker error:', err);
        }
      };

      const buttons = [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Choose Photo', onPress: doPickPhoto },
      ];

      if (hasArtwork) {
        buttons.splice(1, 0, {
          text: 'Remove Artwork',
          style: 'destructive',
          onPress: () => updateTrackArtwork(id, null),
        });
      }

      Alert.alert(
        hasArtwork ? 'Edit Cover Art' : 'Add Cover Art',
        hasArtwork
          ? 'Choose a new photo or remove the current artwork.'
          : 'Pick a photo from your library to show on the lock screen when this track plays.',
        buttons
      );
    },
    [updateTrackArtwork]
  );

  const setTrackCategories = useCallback(
    (id, newCategories) => {
      setTracks((prev) => {
        const next = prev.map((t) =>
          t.id === id ? { ...t, categories: newCategories } : t
        );
        persist(next, categories);
        return next;
      });
    },
    [categories, persist]
  );

  const addCategory = useCallback(
    (name) => {
      setCategories((prev) => {
        if (prev.includes(name)) return prev;
        const next = [...prev, name];
        persist(tracks, next);
        return next;
      });
    },
    [tracks, persist]
  );

  const tracksInCategory = useCallback(
    (categoryName) => tracks.filter((t) => t.categories.includes(categoryName)),
    [tracks]
  );

  /**
   * Flag a track as missing its file so SoundTile switches to warning state
   * immediately, without waiting for the next app launch.
   * Called by SoundTile when a file-existence check fails at play time.
   *
   * Returns a Promise that resolves once the AsyncStorage write has completed.
   * Callers should await this so the flag is durable even if the calling
   * component unmounts before React flushes the state update — the storage
   * write is started synchronously (relative to the async call chain) before
   * any re-render can interfere.
   */
  const markTrackMissing = useCallback(
    async (id) => {
      // Read from the always-current ref so we don't depend on the React
      // state snapshot that was captured when this callback was created.
      const current = tracksRef.current;
      const track = current.find((t) => t.id === id);
      // Skip the update if the flag is already set — avoids a redundant
      // persist and re-render cycle.
      if (!track || track.needsReimport) return;

      const next = current.map((t) =>
        t.id === id ? { ...t, needsReimport: true } : t
      );

      // Kick off the AsyncStorage write BEFORE the React state update so the
      // flag is durable even if the component unmounts mid-render.  We await
      // it here so that any caller that also awaits markTrackMissing is
      // guaranteed the write completed.
      await persist(next, categories);

      // Update React state for immediate UI feedback.  Use the functional
      // form so we don't clobber a concurrent update that may have landed
      // between the ref read above and this setTracks call.
      setTracks((prev) => {
        const existing = prev.find((t) => t.id === id);
        if (!existing || existing.needsReimport) return prev;
        return prev.map((t) =>
          t.id === id ? { ...t, needsReimport: true } : t
        );
      });
    },
    [categories, persist]
  );

  // Subscribe to AudioEngine layer-load errors. When AudioEngine detects a
  // missing file on a *currently playing* layer (e.g. via the foreground-resume
  // check in _checkActiveLayersExist), it fires _errorListeners with a
  // FILE_MISSING error. We translate that into a markTrackMissing call so the
  // tile switches to warning state immediately without requiring a full
  // app restart or user interaction.
  useEffect(() => {
    return AudioEngine.onLayerError(({ id, error }) => {
      if (error?.code === 'FILE_MISSING') {
        markTrackMissing(id).catch((err) =>
          console.warn('[LibraryContext] onLayerError: markTrackMissing storage write failed:', err)
        );
      }
    });
  }, [markTrackMissing]);

  // Keep a stable ref to markTrackMissing so the AppState handler below always
  // calls the latest version without needing to be re-subscribed.
  const markTrackMissingRef = useRef(markTrackMissing);
  useEffect(() => { markTrackMissingRef.current = markTrackMissing; }, [markTrackMissing]);

  // On app foreground, proactively check every idle local-file track to see if
  // its file still exists on disk. This runs regardless of which screen is
  // currently visible, so the warning tile appears on both LibraryScreen and
  // MixerScreen without the user having to navigate to either one first.
  // Tracks already active in AudioEngine are skipped (covered by AudioEngine's
  // own _checkActiveLayersExist on foreground).
  useEffect(() => {
    const scanIdleTracksForMissingFiles = async () => {
      const BATCH_SIZE = 4;
      const idleTracks = tracksRef.current.filter(
        (t) =>
          !t.needsReimport &&
          typeof t.uri === 'string' &&
          t.uri.startsWith('file://') &&
          !AudioEngine.layers[`custom-${t.id}`]
      );

      // Process in small batches to avoid hammering the filesystem with
      // dozens of concurrent stat calls on large libraries. Results from
      // each batch propagate immediately as the batch completes.
      for (let i = 0; i < idleTracks.length; i += BATCH_SIZE) {
        const batch = idleTracks.slice(i, i + BATCH_SIZE);
        await Promise.all(
          batch.map(async (t) => {
            try {
              const info = await FileSystem.getInfoAsync(t.uri);
              if (!info.exists) {
                console.warn('[LibraryContext] idle track file missing on foreground:', t.name, t.uri);
                markTrackMissingRef.current(t.id);
              }
            } catch (err) {
              console.warn('[LibraryContext] foreground file check error for', t.name, err);
            }
          })
        );
      }
    };

    // Run once when the library is first loaded so tiles are accurate immediately.
    if (loaded) {
      scanIdleTracksForMissingFiles();
    }

    // Re-run whenever the app returns to the foreground.
    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active') {
        scanIdleTracksForMissingFiles();
      }
    });

    return () => subscription.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  const value = {
    loaded,
    tracks,
    categories,
    importTrack,
    reimportTrack,
    removeTrack,
    setTrackCategories,
    addCategory,
    tracksInCategory,
    markTrackMissing,
    updateTrackArtwork,
    editTrackArtwork,
  };

  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
}

export function useLibrary() {
  const ctx = useContext(LibraryContext);
  if (!ctx) throw new Error('useLibrary must be used inside a LibraryProvider');
  return ctx;
}
